import gsap from "gsap"
import { MotionPathPlugin } from "gsap/MotionPathPlugin"
import { sounds, isMuted, setMuted } from './modules/sounds.js'
import { initScene } from './modules/scene.js'
import { initParticles } from './modules/particles.js'
import { initRabbit } from './modules/rabbit.js'
import { initOnboarding } from './modules/onboarding.js'
import { initCards } from './modules/cards.js'
import { initAuth } from './modules/auth.js'
import { initUI } from './modules/ui.js'
import { initOracle } from './modules/oracle.js'

gsap.registerPlugin(MotionPathPlugin)

// ── SCENE / PARTICLES / RABBIT ────────────────────────────────────────────────
const { scene, camera, renderer, loader }              = initScene()
const { animateParticles, spawnDust, raycaster, mouseCoords } = initParticles(scene, loader)
const { showRabbit, hideRabbit, speak, stopMouthAnimation }   = initRabbit(isMuted)

// ── ONBOARDING ────────────────────────────────────────────────────────────────
initOnboarding(sounds)

// ── CARDS ─────────────────────────────────────────────────────────────────────
const {
    CARD_COUNT,
    shuffle, revealCard, revealDailyCard,
    typeWriter, resetCards, animateCards, handleCardHover,
    getRevealed, setRevealed, preloadTextures
} = initCards(scene, loader, raycaster, mouseCoords, sounds, () => updateInterface())
preloadTextures()

// ── ENV / SUPABASE ────────────────────────────────────────────────────────────
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY
const N8N_WEBHOOK  = import.meta.env.VITE_N8N_WEBHOOK

document.getElementById('gmp-loader').setAttribute('key', import.meta.env.VITE_GOOGLE_MAPS_KEY)

const supabaseClient = supabase.createClient(SUPABASE_URL, SUPABASE_KEY)

// ── SHARED STATE (synced from auth.js via setters) ────────────────────────────
let currentUser     = null
let userProfileData = null

// ── UI FUNCTIONS (assigned by initUI below) ───────────────────────────────────
let showMagicAlert, closeMagicAlert, checkAgeConsent
let updateInterface, updateButtonStates, updateLimitDisplay, updateLimitUI, animateCoinFlow

// ── ORACLE FUNCTIONS (assigned by initOracle below) ───────────────────────────
let checkLimit, runOracle

// ── TRANSLATIONS ──────────────────────────────────────────────────────────────
const translations = {
    en: {
        placeholder:   "Ask me in any language..",
        charging:      "Charging the magic deck... 0%",
        revealBtn:     "Reveal Cards",
        dailyCardBtn:  "Daily Card",
        thinking:      "The Oracle is studying the cards...",
        ready:         "Your spread is ready.",
        error:         "Connection to the astral plane severed. Try again later.",
        save:          "✨ Save result",
        shortError:    "Your question is too vague. Please provide more detail...",
        invalid:       "The Oracle is silent. Try phrasing your question differently.",
        limitInfo:     "Questions left today: ",
        loadingStages: ["Connecting with the stars...", "Shuffling fate...", "Reading cards...", "Forming an answer..."],
        nameinfo:      "Follow the white rabbit",
        policy:        "© 2026 FOLLOW THE WHITE RABBIT",
    }
}

// ── INPUT HANDLING (bridges particles ↔ cards) ────────────────────────────────
function getMousePos(e) {
    if (e.touches && e.touches.length > 0) return { x: e.touches[0].clientX, y: e.touches[0].clientY }
    return { x: e.clientX, y: e.clientY }
}

function handleMove(event) {
    if (event.type === 'touchmove') event.preventDefault()
    const pos   = getMousePos(event)
    const normX =  (pos.x / window.innerWidth)  * 2 - 1
    const normY = -(pos.y / window.innerHeight) * 2 + 1
    spawnDust(normX, normY, camera)
    handleCardHover(camera)
}

window.addEventListener('mousemove', handleMove)
window.addEventListener('touchstart', handleMove, { passive: false })
window.addEventListener('touchmove',  handleMove, { passive: false })

// ── HISTORY LIST TOUCH FIX ────────────────────────────────────────────────────
document.getElementById('history-list').addEventListener('touchstart', function(e) {
    e.stopPropagation()
}, { passive: true })

// ── RENDER LOOP ───────────────────────────────────────────────────────────────
function animate() {
    requestAnimationFrame(animate)
    const elapsedTime = Date.now() * 0.001
    animateParticles(elapsedTime)
    animateCards(elapsedTime)
    renderer.render(scene, camera)
}
animate()

// ── UI ────────────────────────────────────────────────────────────────────────
;({
    showMagicAlert, closeMagicAlert, checkAgeConsent,
    updateInterface, updateButtonStates, updateLimitDisplay, updateLimitUI, animateCoinFlow
} = initUI(sounds, {
    currentUserFn:     () => currentUser,
    userProfileDataFn: () => userProfileData,
    checkLimit:  (...a) => checkLimit?.(...a),
    runOracle:   (...a) => runOracle?.(...a),
    translations,
}))

updateInterface()

// ── AUTH ──────────────────────────────────────────────────────────────────────
const _auth = initAuth(supabaseClient, {
    sounds,
    checkLimit:        (...a) => checkLimit?.(...a),
    updateButtonStates,
    showMagicAlert,
    setCurrentUser:     (u) => { currentUser = u },
    setUserProfileData: (d) => { userProfileData = d },
    getCurrentUser:     ()  => currentUser,
    getUserProfileData: ()  => userProfileData,
})

// ── ORACLE ────────────────────────────────────────────────────────────────────
;({ checkLimit, runOracle } = initOracle(supabaseClient, {
    N8N_WEBHOOK,
    CARD_COUNT,
    sounds,
    shuffle, revealCard, revealDailyCard, resetCards, getRevealed, setRevealed,
    speak, showRabbit, hideRabbit,
    renderer, scene, camera,
    isMuted, setMuted, stopMouthAnimation,
    showMagicAlert, closeMagicAlert, checkAgeConsent,
    updateButtonStates, updateLimitUI, animateCoinFlow,
    getCurrentUser:     ()     => currentUser,
    getUserProfileData: ()     => userProfileData,
    loadUserProfile:    (...a) => _auth.loadUserProfile(...a),
    signInWithGoogle:   (...a) => _auth.signInWithGoogle(...a),
}))
